import React from 'react';
import BackgroundVideo from '../components/BackgroundVideo';

const Home = () => {
  return (
    <div className="relative min-h-screen bg-black">
      {/* Hero Video Section */}
      <section className="relative h-screen overflow-hidden">
        <BackgroundVideo />

        {/* Hero Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>

        {/* Hero Content */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-shadow animate-fadeIn">
              Ultimate Bar & Grill
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-shadow animate-fadeIn">
              Experience the best food and drinks in Mooresville, NC
            </p>
            <div className="space-y-4 md:space-y-0 md:space-x-4 md:flex md:justify-center animate-fadeIn">
              <a href="/menu" className="btn-primary inline-block">
                View Our Menu
              </a>
              <a href="https://order.toasttab.com/online/ultimate-bar-and-grill-455-east-plaza-drive" target="_blank" rel="noopener noreferrer" className="btn-secondary inline-block">
                Order Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Location Banner */}
      <section className="bg-yellow-500 py-6 text-black">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <span className="text-lg font-semibold">455 East Plaza Drive, Mooresville, NC 28115</span>
          </div>
        </div>
      </section>

      {/* Featured Sections */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Order Online */}
            <div className="relative overflow-hidden rounded-lg bg-cover bg-center h-80"
              style={{ backgroundImage: 'url("/images/backgrounds/food-bg.webp")' }}>
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                <div className="text-center text-white">
                  <h2 className="text-2xl font-bold mb-4">Order Great Food Online</h2>
                  <p className="mb-6">Don't feel like cooking today? We got you covered!</p>
                  <a href="https://order.toasttab.com/online/ultimate-bar-and-grill-455-east-plaza-drive" target="_blank" rel="noopener noreferrer" className="btn-primary">Order Now</a>
                </div>
              </div>
            </div>

            {/* View Menu */}
            <div className="relative overflow-hidden rounded-lg bg-cover bg-center h-80"
              style={{ backgroundImage: 'url("/images/backgrounds/restaurant-bg.webp")' }}>
              <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                <div className="text-center text-white">
                  <h2 className="text-2xl font-bold mb-4">Mooresville's Best Sports Bar Menu</h2>
                  <p className="mb-6">Wings, burgers, sandwiches & more!</p>
                  <a href="/menu" className="btn-primary">View Menu</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section
        className="py-16 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/images/backgrounds/interior-bg.webp")'
        }}
      >
        <div className="container mx-auto px-4">
          <h2 className="section-title mb-12">Our Story - Mooresville's Favorite Bar & Grill</h2>
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-gray-300 text-lg leading-relaxed mb-8">
              <p className="mb-6">
                Ultimate Bar & Grill in Mooresville, NC, is a testament to community revitalization and the power of local ownership. Located at 455 East Plaza Drive, this beloved local spot was formerly known as Ten O'Clock Charlie's—a neighborhood favorite known for its hearty food, laid-back vibe, and friendly service.
              </p>
              <p className="mb-6">
                In 2021, Joe took the reins with a vision: to breathe new life into the bar while preserving its welcoming essence. A familiar face to many in Mooresville, Joe brought passion, purpose, and a strong community focus to the transformation.
              </p>
              <p className="text-yellow-400 font-semibold">
                Today, Ultimate Bar & Grill is more than a place to grab a bite or a drink—it's a space where locals come together, friendships are made, and good times are always on the menu.
              </p>
            </div>
            <a href="/our-story" className="btn-primary inline-block">
              Read Our Full Story
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="section-title mb-8">What Customers Say About Our Food</h2>
          <div className="flex justify-center">
            <div
              className="elfsight-app-b38e84d1-dff7-43b8-84fe-949a7a0441ed"
              data-elfsight-app-lazy
            ></div>
          </div>
        </div>
      </section>

      {/* Contact & Map Section */}
      <section className="py-0 bg-black">
        <div className="grid md:grid-cols-2">
          {/* Contact Info */}
          <div className="bg-gray-900 py-16 px-8">
            <div className="max-w-md mx-auto">
              <h2 className="text-4xl font-bold text-white uppercase mb-6 tracking-wide">Visit Our Sports Bar in Mooresville</h2>
              <div className="space-y-6 text-gray-300">
                <div>
                  <h3 className="text-yellow-500 font-semibold mb-2">Address</h3>
                  <p>455 East Plaza Drive</p>
                  <p>Mooresville, NC 28115</p>
                </div>
                <div>
                  <h3 className="text-yellow-500 font-semibold mb-2">Phone</h3>
                  <a href="tel:+17047999771" className="hover:text-yellow-500 transition-colors">
                    (704) 799-9771
                  </a>
                </div>
                <div>
                  <h3 className="text-yellow-500 font-semibold mb-2">Hours</h3>
                  <p>Mon - Thu: 11am - 10pm</p>
                  <p>Friday: 11am - 12am</p>
                  <p>Saturday: 12pm - 12am</p>
                  <p>Sunday: 12pm - 10pm</p>
                </div>
                <div className="pt-4">
                  <a
                    href="https://order.toasttab.com/online/ultimate-bar-and-grill-455-east-plaza-drive"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-block"
                  >
                    Order Online
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="bg-gray-300 min-h-96 relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3251.8234567890123!2d-80.8234567890123!3d35.5234567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s455%20East%20Plaza%20Drive%2C%20Mooresville%2C%20NC%2028115!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ultimate Bar & Grill Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
